import React from "react";
import { motion } from "framer-motion";
import PlatformSection from "../components/PlatformSection";
import Features from "../components/Features";
import CTA from "../components/CTA";
import Section from "../components/Section";

const integrations = [
  "PMS",
  "POS",
  "Door locks",
  "IPTV",
  "HSI · Wi-Fi",
  "CRM",
  "OTA",
  "Payments",
];

export default function Platform() {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="page-platform"
    >
      <PageHeader
        overline="The platform"
        title={
          <>
            Three products.{" "}
            <span className="grad-text">One hospitality operating system.</span>
          </>
        }
        subtitle="Guest, Staff, and HOP — designed to work in concert. Deployed in days. Certified for enterprise. Built for what hotels really run on."
      />

      <PlatformSection showHeader={false} />
      <Features />

      <Section id="integrations" overline="Integrations" testId="integrations-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <h2
            className="md:col-span-8 font-display text-white text-[36px] md:text-[52px] leading-[1] tracking-tight"
            style={{ fontWeight: 800 }}
          >
            Fits inside your{" "}
            <span className="grad-text">existing stack.</span>
          </h2>
          <p className="md:col-span-4 text-white/70 text-[15px]">
            iNPLASS connects with the systems you already trust — from PMS to
            door locks — with certified integrations.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          {integrations.map((i) => (
            <div
              key={i}
              className="rounded-xl brand-panel px-5 py-6 flex items-center justify-between hover:border-brand-purpleLight/60 transition-colors"
              data-testid={`integration-${i.toLowerCase().replace(/\s/g, "-")}`}
            >
              <span
                className="font-display text-white text-[19px]"
                style={{ fontWeight: 700 }}
              >
                {i}
              </span>
              <span className="font-display text-[10px] uppercase tracking-[0.24em] text-emerald-300 font-bold">
                Connected
              </span>
            </div>
          ))}
        </div>
      </Section>

      <CTA />
    </motion.main>
  );
}

export function PageHeader({ overline, title, subtitle }) {
  return (
    <section className="relative pt-[140px] md:pt-[180px] pb-20 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-purple via-brand-purpleMid to-brand-purpleLight" />
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-brand-orange/30 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-brand-purpleLight/30 blur-3xl" />
        <div className="noise-overlay opacity-60" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        <div className="font-display text-[11px] uppercase tracking-[0.28em] text-white/90 font-bold">
          {overline}
        </div>
        <h1
          className="mt-5 font-display text-white text-[46px] md:text-[74px] leading-[0.98] tracking-tight max-w-4xl text-balance"
          style={{ fontWeight: 800 }}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="mt-6 text-white/90 text-[17px] md:text-[19px] leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}
